import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { SignIn, ArrowRight, UserPlus, List, X, CaretDown } from '@phosphor-icons/react';
import { Button, ActionIcon } from '@mantine/core';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';

function NavDropdown({ label, items, onClose }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const timeout = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => { document.removeEventListener('mousedown', handler); clearTimeout(timeout.current); };
  }, []);

  const handleEnter = () => { clearTimeout(timeout.current); setOpen(true); };
  const handleLeave = () => { timeout.current = setTimeout(() => setOpen(false), 150); };

  return (
    <div ref={ref} className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-[17px] text-black hover:opacity-60 font-medium transition bg-transparent border-none cursor-pointer"
      >
        {label}
        <CaretDown size={14} weight="bold" style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      <div
        className="absolute z-50"
        style={{
          top: 'calc(100% + 8px)',
          left: '50%',
          transform: open ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-4px)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.15s ease, transform 0.15s ease',
        }}
      >
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 min-w-[180px]">
          {items.map(item => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => { setOpen(false); onClose?.(); }}
              className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Hamburger threshold: the desktop nav (logo + 5 links incl. "Droid vs Agencja"
  // + 2 buttons) needs a lot of room, and the nav bar itself caps at ~1100-1200px,
  // so the items get cramped/clipped just above a lower breakpoint. Show the mobile
  // hamburger menu below 1200px to be safe. Initialised from width to avoid a flash.
  const NAV_BREAKPOINT = 1200;
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < NAV_BREAKPOINT);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    const handleResize = () => setIsMobile(window.innerWidth < NAV_BREAKPOINT);
    handleResize();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    return () => { window.removeEventListener('scroll', handleScroll); window.removeEventListener('resize', handleResize); };
  }, []);

  const materialyItems = [
    { to: '/poradniki', label: t('nav.guides') },
    { to: '/ebooki', label: t('nav.ebooks') },
    { to: '/blog', label: t('nav.blog') },
  ];

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 px-3 pt-2 md:px-6 md:pt-2">
        <nav
          className="transition-all duration-300"
          style={{
            maxWidth: scrolled ? 1100 : 1200,
            margin: '0 auto',
            backgroundColor: (scrolled || isMobile) ? 'rgba(255,255,255,0.95)' : 'transparent',
            backdropFilter: (scrolled || isMobile) ? 'blur(12px)' : 'none',
            borderRadius: 16,
            boxShadow: (scrolled || isMobile) ? '0 4px 24px rgba(0,0,0,0.10)' : 'none',
          }}
        >
          <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
            <Link to="/" className="flex items-center">
              <Logo className="h-10" />
            </Link>

            {/* Desktop nav */}
            <div className="items-center gap-6" style={{ display: isMobile ? 'none' : 'flex' }}>
              <Link to="/funkcje" className="text-[17px] text-black hover:opacity-60 font-medium transition">{t('nav.features')}</Link>
              <Link to="/droid-vs-agencja" className="text-[17px] text-black hover:opacity-60 font-medium transition">{t('nav.comparison')}</Link>
              <Link to="/cennik" className="text-[17px] text-black hover:opacity-60 font-medium transition">{t('nav.pricing')}</Link>
              <NavDropdown label={t('nav.materials')} items={materialyItems} />
              <Link to="/kontakt" className="text-[17px] text-black hover:opacity-60 font-medium transition">{t('nav.contact')}</Link>
            </div>

            {/* Desktop buttons */}
            <div className="items-center gap-3" style={{ display: isMobile ? 'none' : 'flex' }}>
              <LanguageSwitcher variant="button" />
              {user ? (
                <Button component={Link} to="/app" color="brand" size="md" radius="md" rightSection={<ArrowRight size={18} weight="bold" />} styles={{ root: { fontSize: 17 } }}>
                  {t('nav.openDashboard')}
                </Button>
              ) : (
                <>
                  <Button
                    component={Link}
                    to="/logowanie"
                    variant="transparent"
                    size="md"
                    radius="md"
                    leftSection={<SignIn size={18} />}
                    styles={{ root: { color: 'black', fontSize: 17 } }}
                  >
                    {t('nav.login')}
                  </Button>
                  <Button component={Link} to="/rejestracja" color="brand" size="md" radius="md" leftSection={<UserPlus size={18} />} styles={{ root: { fontSize: 17 } }}>
                    {t('nav.start')}
                  </Button>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              style={{ display: isMobile ? 'inline-flex' : 'none' }}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
            >
              {menuOpen ? <X size={24} /> : <List size={24} />}
            </ActionIcon>
          </div>
        </nav>
      </div>

      {/* Mobile dropdown menu */}
      {menuOpen && isMobile && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setMenuOpen(false)} />
          <div className="fixed z-[60] bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] p-5 space-y-2 mobile-menu-pop" style={{ top: 88, left: 12, right: 12 }}>
            <Link to="/funkcje" onClick={() => setMenuOpen(false)} className="block text-lg font-semibold text-gray-900 hover:bg-gray-50 rounded-xl px-4 py-3 transition">
              {t('nav.features')}
            </Link>
            <Link to="/droid-vs-agencja" onClick={() => setMenuOpen(false)} className="block text-lg font-semibold text-gray-900 hover:bg-gray-50 rounded-xl px-4 py-3 transition">
              {t('nav.comparison')}
            </Link>
            <Link to="/cennik" onClick={() => setMenuOpen(false)} className="block text-lg font-semibold text-gray-900 hover:bg-gray-50 rounded-xl px-4 py-3 transition">
              {t('nav.pricing')}
            </Link>
            <Link to="/poradniki" onClick={() => setMenuOpen(false)} className="block text-lg font-semibold text-gray-900 hover:bg-gray-50 rounded-xl px-4 py-3 transition">
              {t('nav.guides')}
            </Link>
            <Link to="/ebooki" onClick={() => setMenuOpen(false)} className="block text-lg font-semibold text-gray-900 hover:bg-gray-50 rounded-xl px-4 py-3 transition">
              {t('nav.ebooks')}
            </Link>
            <Link to="/blog" onClick={() => setMenuOpen(false)} className="block text-lg font-semibold text-gray-900 hover:bg-gray-50 rounded-xl px-4 py-3 transition">
              {t('nav.blog')}
            </Link>
            <Link to="/kontakt" onClick={() => setMenuOpen(false)} className="block text-lg font-semibold text-gray-900 hover:bg-gray-50 rounded-xl px-4 py-3 transition">
              {t('nav.contact')}
            </Link>
            <div className="pt-2 space-y-2">
              {user ? (
                <Button component={Link} to="/app" color="brand" size="lg" radius="xl" fullWidth rightSection={<ArrowRight size={18} weight="bold" />} onClick={() => setMenuOpen(false)}>
                  {t('nav.openDashboard')}
                </Button>
              ) : (
                <>
                  <Button component={Link} to="/rejestracja" color="brand" size="lg" radius="xl" fullWidth onClick={() => setMenuOpen(false)}>
                    {t('nav.startFree')}
                  </Button>
                  <Button component={Link} to="/logowanie" variant="default" size="lg" radius="xl" fullWidth onClick={() => setMenuOpen(false)}>
                    {t('nav.login')}
                  </Button>
                </>
              )}
              <div className="flex justify-center pt-1">
                <LanguageSwitcher variant="button" />
              </div>
            </div>
          </div>
        </>
      )}

      <div className="h-[72px]" />
    </>
  );
}
